import React from "react";
import { Avatar, Tooltip } from "@material-ui/core";
import { 
  Smartphone as PhoneIcon, 
  Android as RobotIcon,
  Person as PersonIcon 
} from "@material-ui/icons";
import WhatsAppAvatar from "../WhatsAppAvatar";

// WhatsApp-style color palette for user initials
const getUserAvatarColor = (name) => {
  if (!name) return "#7C7C7C";
  
  // Extract first letter from name, ignoring numbers, emojis, and special characters
  const firstLetter = name.match(/[a-zA-Z]/)?.[0];
  
  if (!firstLetter) {
    // If no letter found, use random color from predefined palette
    const fallbackColors = [
      "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FECA57",
      "#FF9FF3", "#54A0FF", "#5F27CD", "#00D2D3", "#FF9F43",
      "#10AC84", "#EE5A24", "#0984E3", "#6C5CE7", "#A29BFE"
    ];
    const hash = name.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return fallbackColors[Math.abs(hash) % fallbackColors.length];
  }
  
  // Color mapping based on first letter
  const colorMap = {
    'A': '#FF6B6B', 'B': '#4ECDC4', 'C': '#45B7D1', 'D': '#96CEB4', 'E': '#FECA57',
    'F': '#FF9FF3', 'G': '#54A0FF', 'H': '#5F27CD', 'I': '#00D2D3', 'J': '#FF9F43',
    'K': '#10AC84', 'L': '#EE5A24', 'M': '#0984E3', 'N': '#6C5CE7', 'O': '#A29BFE',
    'P': '#FF6B6B', 'Q': '#4ECDC4', 'R': '#45B7D1', 'S': '#96CEB4', 'T': '#FECA57',
    'U': '#FF9FF3', 'V': '#54A0FF', 'W': '#5F27CD', 'X': '#00D2D3', 'Y': '#FF9F43',
    'Z': '#10AC84'
  };
  
  return colorMap[firstLetter.toUpperCase()] || "#7C7C7C";
};

const getUserInitials = (name) => {
  if (!name) return "";
  
  // Split name into words and get first letter of first two words
  const words = name.trim().split(/\s+/);
  let initials = "";
  
  if (words.length >= 1) {
    const firstLetter = words[0].match(/[a-zA-Z]/)?.[0];
    if (firstLetter) initials += firstLetter.toUpperCase();
  }
  
  if (words.length >= 2) {
    const secondLetter = words[1].match(/[a-zA-Z]/)?.[0];
    if (secondLetter) initials += secondLetter.toUpperCase();
  }
  
  return initials;
};

const MessageAvatar = ({ 
  message, 
  size = 32, 
  style = {}, 
  className = "",
  ...props 
}) => {
  const { fromMe, nameUser, contact } = message;
  
  // For incoming messages (from client), show contact avatar
  if (!fromMe) {
    return (
      <Tooltip title={contact?.name || "Contacto"} arrow>
        <WhatsAppAvatar
          src={contact?.profilePicUrl}
          name={contact?.name}
          size={size}
          style={style}
          className={className}
          {...props}
        />
      </Tooltip>
    );
  }
  
  // For outgoing messages (from us)
  if (nameUser === "SYSTEM" || nameUser === "SISTEMA") {
    return (
      <Tooltip title="Enviado automáticamente por el sistema" arrow>
        <Avatar
          style={{ 
            width: size, 
            height: size, 
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            ...style 
          }}
          className={className}
          {...props}
        >
          <RobotIcon style={{ fontSize: size * 0.6 }} />
        </Avatar>
      </Tooltip>
    );
  }
  
  if (nameUser === null || nameUser === undefined) {
    return (
      <Tooltip title="Enviado desde teléfono" arrow>
        <Avatar
          style={{ 
            width: size, 
            height: size, 
            background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
            color: "white",
            ...style 
          }}
          className={className}
          {...props}
        >
          <PhoneIcon style={{ fontSize: size * 0.6 }} />
        </Avatar>
      </Tooltip>
    );
  }
  
  // For messages sent by a specific user
  const initials = getUserInitials(nameUser);
  const backgroundColor = getUserAvatarColor(nameUser);
  
  return (
    <Tooltip title={nameUser} arrow>
      <Avatar
        style={{ 
          width: size, 
          height: size, 
          background: `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}CC 100%)`,
          color: "white",
          fontWeight: "bold",
          fontSize: size * 0.35,
          ...style 
        }}
        className={className}
        {...props}
      >
        {initials || <PersonIcon style={{ fontSize: size * 0.5 }} />}
      </Avatar>
    </Tooltip>
  );
};

export default MessageAvatar;

